import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Navigation } from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrackingPage({ user }) {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooking();
    // Simulate real-time tracking (in production, use WebSocket)
    const interval = setInterval(() => {
      simulateDriverMovement();
    }, 3000);

    return () => clearInterval(interval);
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
      if (response.data.driver_location) {
        setDriverLocation(response.data.driver_location);
      } else {
        // Set initial driver location near pickup
        setDriverLocation({
          lat: response.data.pickup_location.lat + 0.01,
          lng: response.data.pickup_location.lng + 0.01,
        });
      }
    } catch (error) {
      toast.error("Failed to fetch booking");
    } finally {
      setLoading(false);
    }
  };

  const simulateDriverMovement = () => {
    // Simulate driver moving towards pickup/dropoff
    setDriverLocation((prev) => {
      if (!prev || !booking) return prev;
      const target = booking.pickup_location;
      return {
        lat: prev.lat + (target.lat - prev.lat) * 0.1,
        lng: prev.lng + (target.lng - prev.lng) * 0.1,
      };
    });
  };

  if (loading || !booking) {
    return (
      <div className="tracking-page">
        <p>Loading...</p>
      </div>
    );
  }

  const mapCenter = [
    (booking.pickup_location.lat + booking.dropoff_location.lat) / 2,
    (booking.pickup_location.lng + booking.dropoff_location.lng) / 2,
  ];

  const pickupIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const dropoffIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const driverIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  return (
    <div className="tracking-page" data-testid="tracking-page">
      <div className="tracking-container">
        <div className="tracking-header">
          <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-button">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1>Track Your Ride</h1>
            <Badge>{booking.status}</Badge>
          </div>
        </div>

        <div className="tracking-content">
          <div className="map-section">
            <MapContainer
              center={mapCenter}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
              data-testid="map-container"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <Marker position={[booking.pickup_location.lat, booking.pickup_location.lng]} icon={pickupIcon}>
                <Popup>Pickup: {booking.pickup_location.address}</Popup>
              </Marker>
              <Marker position={[booking.dropoff_location.lat, booking.dropoff_location.lng]} icon={dropoffIcon}>
                <Popup>Dropoff: {booking.dropoff_location.address}</Popup>
              </Marker>
              {driverLocation && (
                <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
                  <Popup>Driver Location</Popup>
                </Marker>
              )}
              <Polyline
                positions={[
                  [booking.pickup_location.lat, booking.pickup_location.lng],
                  [booking.dropoff_location.lat, booking.dropoff_location.lng],
                ]}
                color="blue"
                weight={3}
                opacity={0.5}
                dashArray="10, 10"
              />
            </MapContainer>
          </div>

          <Card className="booking-info-card">
            <CardHeader>
              <CardTitle>Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="booking-info">
              <div className="info-row">
                <MapPin size={16} className="text-green-600" />
                <div>
                  <p className="label">Pickup</p>
                  <p>{booking.pickup_location.address}</p>
                </div>
              </div>
              <div className="info-row">
                <MapPin size={16} className="text-red-600" />
                <div>
                  <p className="label">Dropoff</p>
                  <p>{booking.dropoff_location.address}</p>
                </div>
              </div>
              <div className="info-row">
                <Navigation size={16} />
                <div>
                  <p className="label">Vehicle</p>
                  <p>{booking.vehicle_type.toUpperCase()}</p>
                </div>
              </div>
              <div className="info-row">
                <p className="label">Price</p>
                <p className="price">£{booking.price.toFixed(2)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
